import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Trash2, History } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { fmtNum, timeAgo } from "../config";
import { Spinner, Empty, Avatar, SectionTitle } from "../components/cards";
import { SignInNotice } from "./Account";

const PastRow = ({ s, canDelete, onDelete, deleting }) => {
  const navigate = useNavigate();
  return (
    <div className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:flex-row" data-testid={`past-live-${s.id}`}>
      <Link to={`/app/live/${s.id}`} className="relative block w-full shrink-0 overflow-hidden rounded-xl bg-surface sm:w-56">
        <img src={`https://i.ytimg.com/vi/${s.youtubeId}/hqdefault.jpg`} alt={s.title} className="aspect-video h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white">Past live</span>
      </Link>
      <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
        <div>
          <Link to={`/app/live/${s.id}`}>
            <p className="font-heading text-sm font-semibold text-white hover:text-crimson">{s.title}</p>
          </Link>
          <Link to={`/app/creator/${s.creator?.id || s.creatorId}`} className="mt-1.5 flex items-center gap-2 text-xs text-slate-400 hover:text-white">
            <Avatar user={s.creator} size={22} />
            {s.creator?.displayName || s.creator?.username}
          </Link>
        </div>
        <p className="mt-2 text-xs text-slate-500">Streamed {timeAgo(s.createdAt)}{s.endedAt ? ` · ended ${timeAgo(s.endedAt)}` : ""}</p>
      </div>
      {canDelete && (
        <div className="flex items-center sm:flex-col sm:justify-center sm:px-2">
          <button
            onClick={() => onDelete(s)}
            disabled={deleting === s.id}
            data-testid={`past-live-delete-${s.id}`}
            title="Delete this broadcast"
            className="rounded-full border border-crimson/40 p-2.5 text-crimson transition-colors hover:bg-crimson/10 disabled:opacity-50"
          >
            <Trash2 size={15} />
          </button>
        </div>
      )}
    </div>
  );
};

export const PastLive = () => {
  const { user } = useAuth();
  const [streams, setStreams] = useState(null);
  const [tab, setTab] = useState("all");
  const [deleting, setDeleting] = useState(null);
  const [note, setNote] = useState("");

  const load = () =>
    api.get("/live/streams").then((r) => setStreams(Array.isArray(r) ? r : [])).catch(() => setStreams([]));

  useEffect(() => {
    load();
  }, []);

  if (streams === null) return <Spinner />;
  if (!user) return <SignInNotice what="manage past broadcasts" />;

  const isAdmin = user.isAdmin || user.isSuperAdmin;
  const past = streams.filter((s) => !(s.isLive || s.status === "live"));
  const mine = past.filter((s) => (s.creator?.id || s.creatorId) === user.id);
  const shown = tab === "mine" ? mine : past;

  const del = async (s) => {
    setDeleting(s.id);
    setNote("");
    try {
      await api.del(`/live/streams/${s.id}`);
      setStreams((list) => list.filter((x) => x.id !== s.id));
    } catch (err) {
      setNote(err.message || "Couldn't delete this broadcast.");
    }
    setDeleting(null);
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="pastlive-page">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><History size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white">Past Live</h1>
          <p className="text-sm text-slate-500">Find and replay past broadcasts{isAdmin ? ", and manage them as an admin" : ""}.</p>
        </div>
      </div>

      <div className="mb-6 flex gap-1 rounded-2xl border border-white/10 bg-white/[0.02] p-1" data-testid="pastlive-tabs">
        {[["all", "All broadcasts"], ["mine", "My broadcasts"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`pastlive-tab-${key}`}
            className={`flex-1 rounded-xl px-4 py-2.5 font-heading text-xs font-bold transition-colors ${tab === key ? "bg-crimson text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {note && <p className="mb-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300" data-testid="pastlive-note">{note}</p>}

      {shown.length === 0 ? (
        <Empty>{tab === "mine" ? "You haven't ended any broadcasts yet." : "No past broadcasts yet."}</Empty>
      ) : (
        <div className="space-y-3">
          {shown.map((s) => (
            <PastRow
              key={s.id}
              s={s}
              canDelete={isAdmin || (s.creator?.id || s.creatorId) === user.id}
              onDelete={del}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PastLive;
